---
title: "Chosen FAQ"
description: "Quick answers to the questions recruiters ask most about Chosen — careers pages, duplicate candidates, match scores, outreach sending, exports, and more."
category: "reference"
keywords:
  - "Chosen FAQ"
  - "ATS frequently asked questions"
  - "recruiting software help"
  - "Chosen common questions"
schema: "FAQPage"
nav:
  section: "reference"
  order: 20
---

Short answers to the questions that come up most. Each one links to the page with the full story.

## Why isn't my careers page showing?

A careers page only goes live once you publish it — a job that's drafted but unpublished isn't visible. Check that both the organization's careers page and the specific job are published. See [publishing jobs](/guides/careers/publish-jobs).

## Why did the same candidate appear twice?

Chosen deduplicates by the resume file. The same person applying with a *different* file — a re-exported PDF, an updated version — reads as a new candidate. It's expected, not a bug, and you merge the two by hand. See [duplicates and merging](/guides/candidates/duplicates-and-merging).

## Does editing a job description change match scores?

Yes. Match ratings are derived from the job description, so editing it rebuilds the requirements and recomputes every candidate's score for that job. That's correct behavior — but it also stales any [saved search](/guides/search/saved-searches) that sorts on match rating. See [match rating](/guides/candidates/match-rating).

## Why didn't my outreach send?

Most often the prospect had no confirmed email yet, so the message queued and waits for [enrichment](/guides/sourcing/enrichment) to resolve an address. Sending also needs [Gmail connected](/getting-started/integrations/gmail), and Chosen paces sends rather than firing them all at once. See [sending outreach](/guides/sourcing/outreach-sending).

## Why does my search return no results?

Usually too many filters stacked with `AND`, so nothing satisfies all of them at once. Remove a clause and the results come back. [Why a search returns no results](/guides/search/why-no-results) covers the other causes.

## Can I export my data?

Yes. The candidates table exports to CSV (every parsed field) or a ZIP of the original resumes, both scoped to your current filter. See [bulk import and export](/guides/candidates/bulk-import-and-export) and [data and exports](/reference/data-and-exports).

## Why is the scheduler wrong about my availability?

The AI scheduler reads your [Google Calendar](/getting-started/integrations/google-calendar) and only offers times inside your [availability windows](/guides/scheduling/availability-windows). If it's offering bad slots, the calendar isn't connected or the windows aren't set.

## Did Choosie change something without asking?

No. Choosie answers questions immediately, but any *change* — moving a candidate, sending an email, closing a job — is written up as a plan and waits for your approval. Nothing happens until you approve it. See [plans and approvals](/choosie/plans-and-approvals).

## How much does Chosen cost?

Nothing. The ATS is free — unlimited jobs, seats, and candidates. We make our money on marketplace placements, not subscriptions.

## Why was a resume's data parsed wrong?

[Resume parsing](/guides/candidates/resume-parsing) is an LLM reading a document, and it's occasionally wrong — usually on unusual job titles or dense layouts. Every parsed field is editable, so correct it on the candidate and move on.

## Does search look at the open web?

No. Smart, boolean, and deep search all run against candidates already in Chosen. Finding new people on the web is a separate flow — [sourcing](/guides/sourcing).
